import { isAfter, isValid, startOfDay } from 'date-fns';
import { useMemo } from 'react';
import { gql, useQuery } from 'urql';

import { Maybe, PlayerRewardStatus } from '@/globalTypes';
import formatDate from '@/utils/formatter/formatDate';
import {
  PlayerRewardCalculatorQuery,
  PlayerRewardCalculatorQueryVariables,
} from './__generated__/usePlayerRewardCalculator';

const QUERY = gql`
  query PlayerRewardCalculator($playerId: ID!) {
    player(playerId: $playerId) {
      playerRewards {
        edges {
          node {
            assignedAt
            status
          }
        }
      }
      calculateReward: calculateRewardV2 {
        currency
        playerCurrentSegment
        ngr
        ngrSinceLastManualReward
        ineligibilityReasons
        lastBonusAt
        reward {
          rewardAmount
          wagering
          maxWin
          nrOfSpins
          spinsValue
        }
      }
    }
  }
`;

const doesPlayerHaveActiveBonus = (
  playerRewards: PlayerRewardCalculatorQuery['player']['playerRewards'],
) => {
  const startOfDayDate = startOfDay(new Date());

  return (
    playerRewards?.edges?.some((edge) => {
      const status = edge?.node.status;
      if (
        status !== PlayerRewardStatus.Credited &&
        status !== PlayerRewardStatus.Unclaimed
      ) {
        return false;
      }

      const assignedAt = edge?.node.assignedAt;
      if (!assignedAt) {
        return false;
      }

      const assignedAtDate = new Date(assignedAt);
      if (!isValid(assignedAtDate)) {
        return false;
      }

      return (
        isAfter(assignedAtDate, startOfDayDate) ||
        assignedAtDate.getTime() === startOfDayDate.getTime()
      );
    }) ?? false
  );
};

const checkIfEligibleForReward = (
  playerCalculatedReward: Maybe<
    PlayerRewardCalculatorQuery['player']['calculateReward']
  >,
) => {
  if (!playerCalculatedReward) {
    return false;
  }

  return playerCalculatedReward.ineligibilityReasons.length === 0;
};

export const usePlayerRewardCalculator = (playerId: string) => {
  const [{ data, fetching }] = useQuery<
    PlayerRewardCalculatorQuery,
    PlayerRewardCalculatorQueryVariables
  >({
    query: QUERY,
    variables: {
      playerId,
    },
  });

  const calculateNewReward = data?.player.calculateReward ?? null;
  const playerRewards = data?.player.playerRewards ?? null;

  const playerRewardStats = useMemo(() => {
    const stats = calculateNewReward;

    const lastBonusWasToday = playerRewards
      ? doesPlayerHaveActiveBonus(playerRewards)
      : false;

    const ineligibilityReasons = stats?.ineligibilityReasons ?? [];

    const isEligibleForReward = checkIfEligibleForReward(stats);

    return {
      ...stats,
      currency: stats?.currency || 'EUR',
      playerCurrentSegment: stats?.playerCurrentSegment || null,
      ngr: stats?.ngr || null,
      lastBonusAt: formatDate(stats?.lastBonusAt),
      ngrSinceLastManualReward: stats?.ngrSinceLastManualReward || null,
      ineligibilityReasons,
      lastBonusWasToday,
      isEligibleForReward,
    };
  }, [calculateNewReward, playerRewards]);

  return {
    data: {
      ...calculateNewReward?.reward,
      playerRewardStats,
    },
    fetching,
  };
};
